import {useSearchParams} from "react-router";
import {RatingProfileId, SearchState} from "../models/models";
import {
    MapInfo,
    SearchQueryTag,
    SearchQueryTagType
} from "../models/search_query";
import {useCallback, useMemo} from "react";
import {latLng, latLngBounds} from "leaflet";
import {geoLocationFromSearchQueryTagValue} from "./helpers";



const searchMapEl = document.querySelector('[data-controller="search-map"]') as HTMLElement | null;
const domPublicationFilterId = searchMapEl?.dataset.publicationFilterId ?? null;
const domPublicationFilterName = searchMapEl?.dataset.publicationFilterName ?? null;
const domInitialMapBoundsStr = searchMapEl?.dataset.initialMapBounds ?? null;

export function useInIframe(): boolean {
    return searchMapEl?.dataset.inIframe === "true";
}

export function useSearchState(): [ SearchState, (newSearchState: SearchState) => void ] {
    const [searchParams, setSearchParams] = useSearchParams();

    const tagsQuery = searchParams.get("tags") ?? "";
    const placeQueryTag = geoLocationFromSearchQueryTagValue(searchParams.get("center"));
    const { lat, lng } = placeQueryTag ? placeQueryTag : { lat: undefined, lng: undefined }
    const zoom = searchParams.has("zoom") ? parseInt(searchParams.get("zoom") || "16") : 16;
    const ratingProfileId: RatingProfileId = searchParams.has("rating_profile_id") ? parseInt(searchParams.get("rating_profile_id") || "") : null;

    const searchState: SearchState = useMemo(() => {
        const tagValues = tagsQuery.split(";")
        const urlTags = tagValues.filter(Boolean).map(val => {
            const [ type, value ] = val.split(":")
            const tag: SearchQueryTag = { type: type as SearchQueryTagType, value: decodeURIComponent(value) };
            return tag;
        });

        const searchQueryTags: SearchQueryTag[] = urlTags.filter(t => t.type !== "publication_filter");
        if (domPublicationFilterId && domPublicationFilterName) {
            searchQueryTags.unshift({ type: "publication_filter", value: `${domPublicationFilterName}/${domPublicationFilterId}`, dismissable: false });
        }

        const parsedBounds = domInitialMapBoundsStr
            ? JSON.parse(domInitialMapBoundsStr) as { north_east: [number, number]; south_west: [number, number] }
            : null;

        let mapInfo: MapInfo = parsedBounds
            ? { bounds: latLngBounds(parsedBounds.south_west, parsedBounds.north_east) }
            : {};
        if (lat && lng) {
            mapInfo = { center: latLng(lat, lng), zoom: zoom };
        }

        const searchState: SearchState = {
            searchQueryTags: searchQueryTags,
            mapInfo: mapInfo,
            ratingProfileId: ratingProfileId
        }
        return searchState;
    }, [tagsQuery, lat, lng, zoom, ratingProfileId]);

    const setSearchState = useCallback((newSearchState: SearchState) => {
        setSearchParams((params) => {
            const { searchQueryTags, mapInfo, ratingProfileId } = newSearchState;

            // serialize SearchQueryTagList and set params
            const values = searchQueryTags.map((tag) => {
                return `${tag.type}:${encodeURIComponent(tag.value)}`;
            }).join(";");

            if (values.length > 0) {
                params.set("tags", values);
            } else {
                params.delete("tags");
            }

            // serialize MapInfo and set params
            if (mapInfo?.center) {
                params.set("center", `${mapInfo.center.lat}/${mapInfo.center.lng}`);
            } else {
                params.delete("center");
            }
            if (mapInfo?.zoom) {
                params.set("zoom", mapInfo.zoom.toString());
            } else {
                params.delete("zoom");
            }

            // serialize ratingProfileId and set params
            if (ratingProfileId === null) {
                params.delete("rating_profile_id");
            } else {
                params.set("rating_profile_id", ratingProfileId.toString());
            }

            return params;
        });
    }, [setSearchParams]);

    return [ searchState, setSearchState ];
}
